/* eslint-disable class-methods-use-this */
import { i18n } from '@lingui/core';
import { compileMessage } from '@lingui/core/compile';
import TranslationInterface from '../graphql/Translations/TranslationInterface';
import OrganizationsStore from '../stores/observables/OrganizationsStore';
import { LOCAL_I18N_FILES_ENABLED } from '../config/env';
import { TranslationData } from '../definitions/schemas';
import { DEFAULT_LANGUAGE } from '../lib/Language';

class I18nService {
  /**
   * @param [anonymizeRequest] optional param to avoid sending a contact id or
   *        authentication header for fetching the org's translations, therefore,
   *        loading them as an anonymous visitor. Used for loading translations
   *        in rooms, as there's not always the context of the visitor.
   */
  public async setLanguage(
    language = DEFAULT_LANGUAGE.value,
    anonymizeRequest = false
  ) {
    i18n.loadLocaleData(language, { plurals: () => 'other' });
    await this.loadTranslation(language, anonymizeRequest);
    i18n.activate(language);
  }

  public async loadTranslation(language: string, anonymizeRequest = false) {
    // hack to get private _messages from i18n class
    // contains the list of loaded messages
    const currentMessages = { ...(i18n as any)._messages };

    // skip load the language, if it is already loaded
    if (Object.prototype.hasOwnProperty.call(currentMessages, language)) {
      return;
    }

    let baseTranslations = {};

    // use json files for local development
    // to avoid to compile lingui files every time that we made a change in the language files
    if (LOCAL_I18N_FILES_ENABLED) {
      const { default: messages } = await import(
        `../locales/${language}/messages.json`
      );
      baseTranslations = messages;
    } else {
      const { messages } = await import(`../locales/${language}/messages.js`);
      baseTranslations = messages;
    }

    const translationsOverrides = await this.loadOrganizationTranslations(
      language,
      anonymizeRequest
    );

    const messages = {
      ...baseTranslations,
      ...translationsOverrides,
    };

    i18n.load(language, messages);
  }

  public async loadOrganizationTranslations(
    language: string,
    anonymizeRequest = false
  ) {
    const currentOrg = OrganizationsStore.getCurrentOrganization();
    if (!currentOrg) {
      return {};
    }

    const translations = await new TranslationInterface(
      undefined,
      anonymizeRequest
    ).get({ language });

    return this.processOrganizationTranslations(translations);
  }

  public processOrganizationTranslations(translations: TranslationData) {
    return Object.keys(translations).reduce(
      (previousValues, key) => ({
        ...previousValues,
        // this method compile the message into lingui CompiledMessage
        [key]: compileMessage(translations[key]),
      }),
      {}
    );
  }
}

// Export a singleton instance
const inst = new I18nService();
export default inst;
